import { typesofUsers } from "../config";

// Statuses where no screening score exists yet at all (role-independent: not even a
// reviewer has a score to look at). Gates the raw score badge via canShowScreeningScore.
const blockedScreeningScoreStatuses = new Set([
  "invited",
  "applicationinprogress",
  "inprogress",
  "submitted",
  "submited",
]);

// Statuses that mean a Docuverus Super Admin Reviewer has NOT yet completed manual
// review (system-generated assessments may still be corrected). Subscribers must not
// see system-determined assessments while the application is in any of these states.
// Superset of blockedScreeningScoreStatuses plus the "in-flight review" state
// (reviewpending). NOTE: 'Screening' (status 4) is NOT pre-review — it is the status
// applied when process screening completes, displayed to everyone as "Screened"
// (mapScreeningDisplayStatus, ticket 3759). This is the single source of truth for
// "pre-review"; subscriber-facing score badges must route through
// canShowScreeningScoreForViewer (not canShowScreeningScore, which is intentionally
// role-independent).
const preReviewStatuses = new Set([
  ...blockedScreeningScoreStatuses,
  "reviewpending",
]);

// The post-submission states where the system has produced assessments that are
// awaiting manual review. Subscribers should see these displayed as "Review Pending"
// (the earlier in-progress/invited states keep their own labels).
const inFlightReviewStatuses = new Set(["reviewpending"]);

const normalizeApplicationStatus = (status: unknown): string => {
  if (typeof status !== "string") {
    return "";
  }

  return status.trim().toLowerCase().replace(/[^a-z]/g, "");
};

export const canShowScreeningScore = (applicationStatus: unknown): boolean => {
  const normalizedStatus = normalizeApplicationStatus(applicationStatus);

  if (normalizedStatus === "") {
    return true;
  }

  return !blockedScreeningScoreStatuses.has(normalizedStatus);
};

const isValidScreeningScoreValue = (score: unknown): boolean => {
  if (score === null || score === undefined) {
    return false;
  }

  if (typeof score === "number") {
    return Number.isFinite(score) && score >= 0;
  }

  if (typeof score === "string") {
    const trimmedScore = score.trim();

    if (trimmedScore === "") {
      return false;
    }

    const normalizedScore = Number(trimmedScore);
    return Number.isFinite(normalizedScore) && normalizedScore >= 0;
  }

  return false;
};

// Submitted normally means screening has not happened yet. Adding a co-applicant
// or guarantor also moves VR status to Application In Progress. A score that
// already exists must stay available from the Result action.
const preservedScreeningReportStatuses = new Set([
  "submitted",
  "submited",
  "applicationinprogress",
  "inprogress",
]);

export const hasPreservedScreeningReport = (
  score: unknown,
  applicationStatus?: unknown,
  hasReviewedReport?: unknown
): boolean =>
  preservedScreeningReportStatuses.has(normalizeApplicationStatus(applicationStatus)) &&
  // Inbox SQL substitutes zero for a missing score; it cannot prove a prior report.
  isValidScreeningScoreValue(score) && (hasReviewedReport === true || Number(score) > 0);

export const hasScreeningScore = (
  score: unknown,
  applicationStatus?: unknown,
  hasReviewedReport?: unknown
): boolean => {
  if (hasPreservedScreeningReport(score, applicationStatus, hasReviewedReport)) {
    return true;
  }

  if (!canShowScreeningScore(applicationStatus)) {
    return false;
  }

  return isValidScreeningScoreValue(score);
};

// Document-analysis screening report action. Status 3 (Submitted) is included only
// when a report already exists; 4/13/14/15 keep the previous screened-status rule.
const screeningReportActionStatusIds = new Set([3, 4, 13, 14, 15]);

export const isScreeningReportActionVisible = (
  applicationStatusId: unknown,
  score: unknown,
  applicationStatus?: unknown,
  viewerRole?: unknown,
  hasReviewedReport?: unknown
): boolean => {
  const statusId = Number(applicationStatusId);
  if (!screeningReportActionStatusIds.has(statusId)) {
    return false;
  }

  return canShowScreeningScoreForViewer(
    score,
    statusId === 3 ? "Submitted" : applicationStatus,
    viewerRole,
    hasReviewedReport
  );
};

// ---------------------------------------------------------------------------
// Story 4622 — Hide System-Determined Assessments Until Manual Review
// ---------------------------------------------------------------------------

// True while the application is still awaiting manual review by a Docuverus Super
// Admin Reviewer (i.e. not yet in a reviewed/terminal status). Unknown/empty status
// is treated as reviewed (show), to stay conservative.
export const isAwaitingManualReview = (applicationStatus: unknown): boolean => {
  const normalizedStatus = normalizeApplicationStatus(applicationStatus);

  if (normalizedStatus === "") {
    return false;
  }

  return preReviewStatuses.has(normalizedStatus);
};

// True when the application is in a post-submission state whose system assessments are
// awaiting manual review (screening / review pending). Used to relabel the status as
// "Review Pending" for subscribers.
export const isInFlightReview = (applicationStatus: unknown): boolean =>
  inFlightReviewStatuses.has(normalizeApplicationStatus(applicationStatus));

// Only the Docuverus Super Admin Reviewer is exempt from the pre-review hiding.
export const isManualReviewerRole = (viewerRole: unknown): boolean =>
  viewerRole === typesofUsers.docuverusSuperAdmin;

// Subscribers (everyone except the reviewer) must not see system-determined
// assessments until manual review is complete.
export const shouldHideSystemAssessment = (
  applicationStatus: unknown,
  viewerRole: unknown
): boolean =>
  isAwaitingManualReview(applicationStatus) && !isManualReviewerRole(viewerRole);

// ---------------------------------------------------------------------------
// Story 4622 extension — Per-Document Review Gate
// ---------------------------------------------------------------------------

const hasExplicitUtcOffset = (value: string): boolean => /Z$|[+-]\d{2}:?\d{2}$/.test(value);

// Parses a date-like value to epoch milliseconds, treating any string with no explicit
// UTC/offset designator (e.g. a raw SQL "YYYY-MM-DD HH:mm:ss" value) as UTC rather than
// letting `new Date(...)` fall back to the browser's local timezone. Without this, comparing
// one endpoint's "Z"-suffixed timestamp against another endpoint's zone-less one can skew the
// result by the viewer's UTC offset.
const toUtcEpochMs = (value: unknown): number => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (typeof value !== "string") return NaN;
  const trimmed = value.trim();
  if (trimmed === "") return NaN;
  if (hasExplicitUtcOffset(trimmed)) return new Date(trimmed).getTime();
  const isoLike = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
  return new Date(`${isoLike}Z`).getTime();
};

// Returns true when the document has been reviewed (its upload predates the last
// Review Done submission). Handles all field-name variants across sections and
// safely tolerates nulls / invalid date strings.
export const isDocumentReviewed = (
  createdOn: string | null | undefined,
  applicationStatusUpdatedOn: string | null | undefined
): boolean => {
  if (!applicationStatusUpdatedOn) return false;
  if (!createdOn) return true; // no date → treat as reviewed (section is empty)
  const docDate = toUtcEpochMs(createdOn);
  const reviewDate = toUtcEpochMs(applicationStatusUpdatedOn);
  if (isNaN(docDate) || isNaN(reviewDate)) return false;
  return docDate <= reviewDate;
};

// Returns true when a subscriber-facing view should show "Review Pending" instead of
// the real status tile / validation messages for this document. Super admins are never
// gated. null/undefined isReviewed is treated as unreviewed (show pending).
export const shouldShowReviewPending = (
  isReviewed: boolean | null | undefined,
  viewerRole: unknown
): boolean => {
  if (isManualReviewerRole(viewerRole)) return false;
  return isReviewed !== true;
};

// ---------------------------------------------------------------------------
// Role-aware score gate for subscriber-facing views. A score badge may be shown only
// when a score exists for this status (hasScreeningScore) AND the viewer is allowed to
// see system-determined assessments for it (i.e. it is reviewed, or the viewer is the
// reviewer). Use this everywhere a non-reviewer might see the screening score.
export const canShowScreeningScoreForViewer = (
  score: unknown,
  applicationStatus?: unknown,
  viewerRole?: unknown,
  hasReviewedReport?: unknown
): boolean => {
  if (hasReviewedReport === true && hasPreservedScreeningReport(score, applicationStatus, hasReviewedReport)) {
    return true;
  }
  return (
    hasScreeningScore(score, applicationStatus, hasReviewedReport) &&
    !shouldHideSystemAssessment(applicationStatus, viewerRole)
  );
};

// True only while a previously reviewed report is waiting to be re-processed.
// The API sets HasPreservedScreeningReport for Submitted and In Progress rows
// that still point at PreservedScreeningReportID. Process screening clears that id.
export const SCREENING_REPORT_REPROCESSING_MESSAGE =
  "A rental decision cannot be applied until the screening report has completed processing.";

export const isScreeningReportReprocessing = (hasPreservedScreeningReport?: unknown): boolean =>
  hasPreservedScreeningReport === true;
